/**
 * Kingdom Factor Member Marketing Page
 * - Hero with membership value proposition
 * - Kingdom Factor Leadership Model section (Psalm 78:72)
 * - Leadership in Action (3 article links)
 * - Five Ways KF Strengthens Your Leadership (accordion)
 * - Getting Started steps
 * - Matt Aston testimonial
 * - 3 Pillars overview (Coach, Peer Group, Leadership Model)
 * - FAQ
 * - Final CTA
 */

import { SeoHead } from "@/components/SeoHead"
import { ArrowRight, Check, Users, Target, BookOpen, Award, MessageCircle, ChevronDown, Calendar, Download, Star } from "lucide-react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"
import { BeFeaturedDialog } from "@/components/BeFeaturedDialog"
import { GetStartedDialog } from "@/components/GetStartedDialog"

export default function Member({ seo, settings = {} }) {
  const { scheduleCallUrl = "#", executiveSummaryUrl = "#" } = settings
  const [openFaq, setOpenFaq] = useState(null)
  const [openBenefit, setOpenBenefit] = useState(0)
  const [featureDialogOpen, setFeatureDialogOpen] = useState(false)
  const [briefingDialogOpen, setBriefingDialogOpen] = useState(false)

  const coreBenefits = [
    {
      icon: Award,
      title: "Coaching That Develops the Whole Leader",
      content: [
        "You are supported by a Kingdom Factor Coach who is specially trained and equipped to guide leaders using the Kingdom Factor Leadership Model, helping you grow personally, spiritually, and operationally.",
      ],
    },
    {
      icon: Users,
      title: "Collaboration That Removes Leadership Isolation",
      content: [
        "Leadership can be isolating. Kingdom Factor walks alongside you as you navigate the real challenges of life and business, helping you become the leader God is calling you to be.",
      ],
    },
    {
      icon: MessageCircle,
      title: "Community That Builds Trusted Relationships",
      content: [
        "You become part of a powerful Christian Peer Advisory Group of experienced business leaders where real conversations about real issues produce real results.",
      ],
    },
    {
      icon: BookOpen,
      title: "Content That Integrates Faith and Leadership",
      content: [
        "You gain access to Kingdom Factor's cutting-edge leadership content designed to help you grow in:",
      ],
      bulletPoints: [
        "Personal leadership development",
        "A biblical worldview of business and leadership",
        "Operational excellence in building thriving organizations",
      ],
    },
    {
      icon: Target,
      title: "Challenge That Creates Real Accountability",
      content: [
        "Kingdom Factor challenges you to focus on what matters most. Each year you will identify and accomplish 12 major goals—called Imperatives—that move your business, leadership, and life forward.",
      ],
    },
  ]

  const pillars = [
    {
      number: "01",
      title: "Your KF Coach",
      subtitle: "Aligning Leadership with Faith",
      description: "Your Kingdom Factor Coach provides guidance and accountability to help you grow professionally, personally, and spiritually while applying the Kingdom Factor Leadership Model to the real challenges you face as a leader.",
      points: [
        "Leadership guidance and coaching",
        "Faith-integrated goal setting",
        "Personal and leadership development",
        "Accountability and follow-through",
      ],
    },
    {
      number: "02",
      title: "Peer Advisory Group",
      subtitle: "United in Faith, Strengthened in Purpose",
      description: "Kingdom Factor Peer Advisory Groups bring together experienced Christian business leaders who share challenges, gain wise counsel, and grow together in a confidential environment. Together, the leaders in the room often represent hundreds of years of real-world business leadership experience.",
      points: [
        "Monthly group meetings",
        "Structured around the KF Leadership Model",
        "Honest conversations about real leadership issues",
        "Diverse leadership and industry perspectives",
      ],
    },
    {
      number: "03",
      title: "KF Leadership Model",
      subtitle: "A Framework for Kingdom Leadership",
      description: "The Kingdom Factor Leadership Model is built on Psalm 78:72—a complete biblical model of leadership. It strengthens relational leadership, personal integrity, and operational excellence—developing leaders who lead the right way, not just the efficient way.",
      points: [
        "Relational Leadership — Shepherding people with care, courage, and accountability",
        "Personal Integrity — Leading from a heart aligned with God's truth",
        "Operational Excellence — Running your organization with clarity, discipline, and skill",
      ],
    },
  ]

  const faqs = [
    {
      question: "What is the group's primary focus and objective?",
      answer: "Kingdom Factor peer groups focus on helping Christian business leaders grow professionally, personally, and spiritually. We integrate faith-based principles with proven business strategies to help members achieve breakthrough results while honoring God in their work.",
    },
    {
      question: "Who are the members of the group?",
      answer: "Kingdom Factor members are experienced Christian business leaders, including business owners, executives, and entrepreneurs who desire to grow both their leadership and their faith. Members come from a variety of industries but share a common commitment to leading their businesses with integrity and a biblical worldview.",
    },
    {
      question: "What is the format of the meetings?",
      answer: "Each Kingdom Factor meeting follows the Kingdom Factor Leadership Model, creating a structured environment where members can grow personally, spiritually, and operationally. Meetings typically include time for prayer, leadership development through faith-integrated business content, peer counsel on real leadership challenges, and accountability for the goals each member is pursuing.",
    },
    {
      question: "How often does the group meet?",
      answer: "Groups meet once each month for a focused four-hour session, either in person or virtually, depending on the group.",
    },
    {
      question: "What is the commitment level expected?",
      answer: "Members commit to attending monthly meetings, participating actively in discussions, completing assignments between sessions, and maintaining confidentiality. The typical commitment is 12 months to allow for meaningful relationship building and growth.",
    },
    {
      question: "How is confidentiality handled?",
      answer: "Confidentiality is paramount. All members sign a confidentiality agreement, and what's shared in the group stays in the group. This creates a safe environment for honest, vulnerable discussions about real challenges.",
    },
  ]

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="member" scheduleCallUrl={scheduleCallUrl} />

        {/* Hero Section */}
        <section className="relative bg-white pt-32 pb-24 overflow-hidden">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-kf-red font-semibold uppercase tracking-wider mb-6">
              Kingdom Factor Membership
            </p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-zinc-900 mb-8 leading-tight">
              THE HIGHER YOU RISE IN LEADERSHIP,{" "}
              <span className="text-kf-red">THE FEWER PEOPLE SPEAK TRUTH TO YOU.</span>
            </h1>
            <p className="text-xl text-zinc-600 leading-relaxed max-w-3xl mx-auto">
              And yet—the more people depend on you getting it right. Those you lead
              don't just need your success—<strong className="text-zinc-900">they need the strongest version of you.</strong>
            </p>
            <p className="text-lg text-zinc-500 leading-relaxed mt-4 max-w-3xl mx-auto">
              Kingdom Factor is a trusted community of Christian business leaders committed
              to sharpening one another, integrating faith with leadership, and finishing
              well—together.
            </p>
            <Button variant="gradient" size="lg" className="mt-10" onClick={() => setBriefingDialogOpen(true)}>
              <Calendar className="w-5 h-5" />
              Schedule Your Intro Call
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </section>

        {/* Kingdom Factor Leadership Model */}
        <section className="bg-kf-black py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-kf-red font-semibold uppercase tracking-wider mb-4">
              The Kingdom Factor Leadership Model
            </p>
            <blockquote className="font-display text-2xl md:text-3xl text-white leading-relaxed mb-4">
              "And David shepherded them with integrity of heart;
              with skillful hands he led them."
            </blockquote>
            <p className="text-white/50 mb-12">— Psalm 78:72</p>

            <p className="text-white/80 text-lg max-w-3xl mx-auto mb-12">
              Kingdom Factor is built on this complete biblical model of leadership.
            </p>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {[
                {
                  title: "Relational Leadership",
                  description: "Shepherding people with care, courage, and accountability.",
                },
                {
                  title: "Personal Integrity",
                  description: "Leading from a heart aligned with God's truth.",
                },
                {
                  title: "Operational Excellence",
                  description: "Running your organization with clarity, discipline, and skill.",
                },
              ].map((pillar, i) => (
                <div
                  key={i}
                  className="bg-white/10 backdrop-blur-sm p-8 rounded-lg border border-white/10 text-center"
                >
                  <h3 className="font-display text-xl text-white mb-2">{pillar.title}</h3>
                  <p className="text-white/70">{pillar.description}</p>
                </div>
              ))}
            </div>

            <p className="text-white/60 text-lg max-w-2xl mx-auto italic">
              Sustainable impact requires not just strategy, but the right leader leading{" "}<span className="whitespace-nowrap">the right way.</span>
            </p>
          </div>
        </section>

        {/* Leadership in Action */}
        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-kf-red font-semibold uppercase tracking-wider mb-3">
                Leadership in Action
              </p>
              <h2 className="font-display text-3xl md:text-4xl text-zinc-900">
                REAL EXAMPLES OF KINGDOM FACTOR LEADERSHIP
              </h2>
              <p className="text-zinc-600 mt-4 max-w-2xl mx-auto">
                Real examples of how Kingdom Factor leaders strengthen their personal
                leadership, integrity, and operational effectiveness.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { name: "Perry Hines", vimeoId: "1174798576" },
                { name: "Julie Spann-Johnson", vimeoId: "1174797444" },
                { name: "Jeremy Allen", vimeoId: "1174798554" },
              ].map((video) => (
                <div key={video.vimeoId} className="bg-zinc-50 rounded-lg overflow-hidden shadow-sm">
                  <div className="aspect-video">
                    <iframe
                      src={`https://player.vimeo.com/video/${video.vimeoId}?title=0&byline=0&portrait=0`}
                      className="w-full h-full"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title={video.name}
                    />
                  </div>
                  <div className="p-4 text-center">
                    <p className="font-semibold text-zinc-900">{video.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Value Proposition: 5 Core Benefits - Accordion Layout */}
        <section id="value" className="bg-kf-gradient py-24 overflow-hidden">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-white/70 font-semibold uppercase tracking-wider mb-3">The KF Promise</p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white mb-4">
                THE FIVE WAYS KINGDOM FACTOR STRENGTHENS YOUR LEADERSHIP
              </h2>
            </div>

            {/* Accordion */}
            <div className="space-y-3">
              {coreBenefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenBenefit(openBenefit === index ? null : index)}
                    className="w-full px-6 py-5 text-left flex items-center gap-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                      <benefit.icon className="w-5 h-5 text-kf-red" />
                    </div>
                    <span className="font-display text-base md:text-lg text-white flex-1 min-w-0">{benefit.title}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-white/60 flex-shrink-0 transition-transform duration-200 ${
                        openBenefit === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {openBenefit === index && (
                    <div className="px-6 pb-6 pt-2">
                      <div className="pl-0 md:pl-14 space-y-4">
                        {benefit.content.map((paragraph, pIndex) => (
                          <p key={pIndex} className="text-white/90 leading-relaxed">
                            {paragraph}
                          </p>
                        ))}
                        {benefit.bulletPoints && (
                          <ul className="space-y-2 pl-4">
                            {benefit.bulletPoints.map((point, bIndex) => (
                              <li key={bIndex} className="text-white/90 flex items-center gap-2">
                                <Check className="w-4 h-4 text-white/70 flex-shrink-0" />
                                {point}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works - White background */}
        <section id="how-it-works" className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-kf-red font-semibold uppercase tracking-wider mb-3">Getting Started</p>
              <h2 className="font-display text-4xl md:text-5xl text-zinc-900 mb-4">
                GETTING STARTED WITH <span className="text-kf-red">KINGDOM FACTOR</span>
              </h2>
              <p className="text-zinc-600 max-w-2xl mx-auto text-lg">
                Explore Kingdom Factor and connect with a trusted network of Christian business leaders.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Step 01 */}
              <div className="text-center flex flex-col items-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-kf-gradient text-white font-display text-2xl rounded-full mb-6">
                  01
                </div>
                <h3 className="font-display text-xl text-zinc-900 mb-3">Download the Executive Summary</h3>
                <p className="text-zinc-600 flex-1">Understand the proven impact of Christian peer advisory groups.</p>
                <div className="mt-6 w-full sm:w-auto">
                  <Button variant="gradient" className="w-full sm:w-auto group" asChild>
                    <a href={executiveSummaryUrl} target="_blank" rel="noopener noreferrer">
                      <Download className="w-4 h-4" />
                      Download Now
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                </div>
              </div>

              {/* Step 02 */}
              <div className="text-center flex flex-col items-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-kf-gradient text-white font-display text-2xl rounded-full mb-6">
                  02
                </div>
                <h3 className="font-display text-xl text-zinc-900 mb-3">Be Featured in the Kingdom Factor Collective</h3>
                <p className="text-zinc-600 flex-1">Share how your faith influences your leadership and business journey.</p>
                <div className="mt-6 w-full sm:w-auto">
                  <Button variant="gradient" className="w-full sm:w-auto group" onClick={() => setFeatureDialogOpen(true)}>
                    <Star className="w-4 h-4" />
                    Get Featured
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>

              {/* Step 03 */}
              <div className="text-center flex flex-col items-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-kf-gradient text-white font-display text-2xl rounded-full mb-6">
                  03
                </div>
                <h3 className="font-display text-xl text-zinc-900 mb-3">Attend a Live Informational Briefing</h3>
                <p className="text-zinc-600 flex-1">Learn how Kingdom Factor peer groups work and explore next steps.</p>
                <div className="mt-6 w-full sm:w-auto">
                  <Button variant="gradient" className="w-full sm:w-auto group" onClick={() => setBriefingDialogOpen(true)}>
                    <Calendar className="w-4 h-4" />
                    Schedule Now
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial - Light background */}
        <section className="bg-zinc-50 py-24">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <p className="font-display text-2xl md:text-3xl text-zinc-900 leading-relaxed mb-8">
              "Kingdom Factor transformed how I lead—shifting my focus to Christ-centered
              leadership. That shift impacted many lives for good and allowed our company
              to grow from 10 employees to over 1,000."
            </p>
            <div>
              <p className="font-bold text-zinc-900 text-lg">Matt Aston</p>
              <p className="text-zinc-500">Ground Penetrating Radar Systems</p>
            </div>
          </div>
        </section>

        {/* The 3 Pillars - Alternating layout */}
        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-kf-red font-semibold uppercase tracking-wider mb-3">What You Get</p>
              <h2 className="font-display text-4xl md:text-5xl text-zinc-900 mb-4">
                THE THREE <span className="text-kf-red">PILLARS</span>
              </h2>
            </div>

            <div className="space-y-20">
              {pillars.map((pillar, index) => (
                <div
                  key={index}
                  className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center"
                >
                  {/* Identity panel—number, title, subtitle */}
                  <div className={`bg-zinc-50 rounded-2xl p-8 md:p-10 text-left ${
                    index % 2 === 1 ? "lg:order-2" : ""
                  }`}>
                    <span className="font-display text-6xl text-kf-red/20 block mb-2 text-left">
                      {pillar.number}
                    </span>
                    <h3 className="font-display text-2xl md:text-3xl text-zinc-900 mb-2">
                      {pillar.title}
                    </h3>
                    <p className="text-kf-red font-medium text-lg">{pillar.subtitle}</p>
                  </div>

                  {/* Content—description + bullet points */}
                  <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                    <p className="text-zinc-600 text-lg leading-relaxed mb-6">
                      {pillar.description}
                    </p>
                    <ul className="space-y-3">
                      {pillar.points.map((point, i) => (
                        <li key={i} className="flex items-center gap-3 text-zinc-700">
                          <Check className="w-5 h-5 text-kf-red flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ - Light gray background */}
        <section id="faq" className="bg-zinc-50 py-24">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-kf-red font-semibold uppercase tracking-wider mb-3">Questions?</p>
              <h2 className="font-display text-4xl md:text-5xl text-zinc-900">
                FREQUENTLY <span className="text-kf-red">ASKED</span>
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-zinc-50 transition-colors"
                  >
                    <span className="font-medium text-zinc-900">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-kf-red flex-shrink-0 transition-transform ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-zinc-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA - Red gradient */}
        <section className="bg-kf-gradient py-24">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-display text-4xl md:text-5xl text-white mb-6">
              READY TO TRANSFORM<br />YOUR BUSINESS & YOUR LIFE?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Take the first step toward building a business that honors God and makes an eternal impact. Schedule your free intro call today.
            </p>
            <Button variant="kf-secondary" size="lg" className="group" onClick={() => setBriefingDialogOpen(true)}>
              <Calendar className="w-5 h-5" />
              Schedule Your Intro Call
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <p className="text-white/50 text-sm mt-6">
              No commitment required. Let's just talk.
            </p>
          </div>
        </section>

        <Footer activePage="member" />
      </div>

      <BeFeaturedDialog open={featureDialogOpen} onOpenChange={setFeatureDialogOpen} />
      <GetStartedDialog open={briefingDialogOpen} onOpenChange={setBriefingDialogOpen} defaultSource="member_briefing" />
    </>
  )
}
